import { BaseElement } from '../../../base-element';
import type { DogFields, ValidationError } from '../../../../state/form.state';
import type { BreedEligibilityInfo } from '../../../../api/types';
import type { SelectOption } from '../../../ui/dt-select';
import '../../../ui/dt-input';
import '../../../ui/dt-select';
import '../../../ui/dt-section-card';
import { getSectionCardState } from './section-card-state';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sexOptions: SelectOption[] = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

export interface DogSectionChangeDetail {
  section: 'dog';
  field: keyof DogFields;
  path: string;
  value: string;
}

export class DogSection extends BaseElement {
  private _dog: DogFields | null = null;
  private _errors: ValidationError[] = [];
  private _breedEligibility: BreedEligibilityInfo | null = null;

  get dog(): DogFields | null {
    return this._dog;
  }

  set dog(value: DogFields | null) {
    this._dog = value;
    this.updateRender();
  }

  get errors(): ValidationError[] {
    return this._errors;
  }

  set errors(value: ValidationError[]) {
    this._errors = value ?? [];
    this.updateRender();
  }

  get breedEligibility(): BreedEligibilityInfo | null {
    return this._breedEligibility;
  }

  set breedEligibility(value: BreedEligibilityInfo | null) {
    this._breedEligibility = value;
    this.updateRender();
  }

  connectedCallback(): void {
    this.updateRender();
  }

  private getError(path: string): string {
    return this._errors.find((error) => error.field === path)?.message ?? '';
  }

  private getBreedEligibilityMessage(): string {
    if (!this._breedEligibility) {
      return '';
    }

    if (this._breedEligibility.entriesClosed) {
      return 'Entries closed';
    }

    if (
      this._breedEligibility.australianShepherdsEligible &&
      !this._breedEligibility.otherHerdingBreedsEligible
    ) {
      return 'Only Australian Shepherds are currently eligible for pre-entry';
    }

    if (this._breedEligibility.otherHerdingBreedsEligible) {
      return 'Pre-entries for other herding breeds are now open';
    }

    return '';
  }

  private emitChange(field: keyof DogFields, value: string): void {
    const path = `dog.${field}`;
    this.emit<DogSectionChangeDetail>('section-change', {
      section: 'dog',
      field,
      path,
      value,
    });
  }

  private emitBlur(field: keyof DogFields): void {
    const path = `dog.${field}`;
    this.emit('section-blur', { section: 'dog', field, path });
  }

  private attachEventListeners(): void {
    this.$$('dt-input[data-field]').forEach((input) => {
      const field = input.getAttribute('data-field') as keyof DogFields | null;
      if (!field) return;
      input.addEventListener('input', (event: Event) => {
        const detail = (event as CustomEvent<{ value: string }>).detail;
        this.emitChange(field, detail?.value ?? '');
      });
      input.addEventListener('blur', () => this.emitBlur(field));
    });

    const sexSelect = this.$('dt-select[data-field="sex"]') as
      | (HTMLElement & { options: SelectOption[] })
      | null;
    if (sexSelect) {
      sexSelect.options = sexOptions;
      sexSelect.addEventListener('change', (event: Event) => {
        const detail = (event as CustomEvent<{ value: string }>).detail;
        this.emitChange('sex', detail?.value ?? '');
        this.emitBlur('sex');
      });
    }
  }

  private updateRender(): void {
    const dog = this._dog;
    const sectionState = getSectionCardState(['dog'], this._errors);
    const errorCountAttr = sectionState.errorCount > 0 ? `error-count="${sectionState.errorCount}"` : '';
    const completeAttr = sectionState.complete ? 'complete="true"' : '';

    this.render(`
      <dt-section-card
        data-testid="dog-section"
        heading="Dog Information"
        icon="pets"
        ${errorCountAttr}
        ${completeAttr}
      >
        <div class="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          <div data-control-path="dog.ascaRegistrationNumber">
            <dt-input
              label="ASCA Registration #"
              value="${escapeHtml(dog?.ascaRegistrationNumber ?? '')}"
              required
              error="${escapeHtml(this.getError('dog.ascaRegistrationNumber'))}"
              data-field="ascaRegistrationNumber"
              data-path="dog.ascaRegistrationNumber"
            ></dt-input>
          </div>
          <div data-control-path="dog.breed">
            <dt-input
              label="Breed"
              value="${escapeHtml(dog?.breed ?? '')}"
              required
              error="${escapeHtml(this.getError('dog.breed'))}"
              data-field="breed"
              data-path="dog.breed"
              ${this._breedEligibility?.entriesClosed ? 'disabled' : ''}
            ></dt-input>
            ${this.getBreedEligibilityMessage() ? `
              <p class="mt-1 text-xs text-neutral-600">${escapeHtml(this.getBreedEligibilityMessage())}</p>
            ` : ''}
          </div>
          <div data-control-path="dog.sex">
            <dt-select
              label="Sex"
              placeholder="Select..."
              value="${escapeHtml(dog?.sex ?? '')}"
              required
              error="${escapeHtml(this.getError('dog.sex'))}"
              data-field="sex"
              data-path="dog.sex"
            ></dt-select>
          </div>
          <div data-control-path="dog.registeredName">
            <dt-input
              label="Registered Name"
              value="${escapeHtml(dog?.registeredName ?? '')}"
              required
              error="${escapeHtml(this.getError('dog.registeredName'))}"
              data-field="registeredName"
              data-path="dog.registeredName"
            ></dt-input>
          </div>
          <div data-control-path="dog.callName">
            <dt-input
              label="Call Name"
              value="${escapeHtml(dog?.callName ?? '')}"
              required
              error="${escapeHtml(this.getError('dog.callName'))}"
              data-field="callName"
              data-path="dog.callName"
            ></dt-input>
          </div>
          <div data-control-path="dog.dob">
            <dt-input
              label="Date of Birth"
              type="date"
              value="${escapeHtml(dog?.dob ?? '')}"
              required
              error="${escapeHtml(this.getError('dog.dob'))}"
              data-field="dob"
              data-path="dog.dob"
            ></dt-input>
          </div>
          <div data-control-path="dog.color">
            <dt-input
              label="Color"
              value="${escapeHtml(dog?.color ?? '')}"
              data-field="color"
              data-path="dog.color"
            ></dt-input>
          </div>
          <div data-control-path="dog.sire">
            <dt-input
              label="Sire"
              value="${escapeHtml(dog?.sire ?? '')}"
              data-field="sire"
              data-path="dog.sire"
            ></dt-input>
          </div>
          <div data-control-path="dog.dam">
            <dt-input
              label="Dam"
              value="${escapeHtml(dog?.dam ?? '')}"
              data-field="dam"
              data-path="dog.dam"
            ></dt-input>
          </div>
          <div class="md:col-span-2 lg:col-span-3" data-control-path="dog.breeders">
            <dt-input
              label="Breeders"
              value="${escapeHtml(dog?.breeders ?? '')}"
              data-field="breeders"
              data-path="dog.breeders"
            ></dt-input>
          </div>
        </div>
      </dt-section-card>
    `);

    this.attachEventListeners();
  }
}

customElements.define('dt-dog-section', DogSection);
